import { useCallback } from 'react'
import { isEmptyProfile } from '../api/employmentMapper'
import { EMPLOYMENT_USE_API } from '../config'
import { useEmploymentStore } from '../store/useEmploymentStore'
import { EmploymentProfile } from '../types/employment.model'
import { useEmploymentProfilesQuery } from './useEmploymentProfilesQuery'

export interface EmploymentProfiles {
  profilesByUserId: Record<string, EmploymentProfile>
  isLoading: boolean
  /** The profiles could not be read at all, see useEmploymentProfilesQuery. */
  isForbidden: boolean
  /**
   * Applies a partial change. A key present with `undefined` clears that field,
   * a key left out keeps whatever is stored, normal patch semantics, whichever
   * source is behind it.
   */
  saveProfile: (userId: string, patch: Partial<EmploymentProfile>) => void
}

/**
 * The one hook components use for employment data. It hides which side of the
 * `EMPLOYMENT_USE_API` seam is answering, so nothing downstream has to care
 * whether the endpoint is live.
 */
export const useEmploymentProfiles = (): EmploymentProfiles => {
  const {
    profilesByUserId: apiProfiles,
    isLoading,
    isForbidden,
    upsertProfile,
    deleteProfile
  } = useEmploymentProfilesQuery()

  const storeProfiles = useEmploymentStore(state => state.profilesByUserId)
  const setStoreProfile = useEmploymentStore(state => state.setProfile)
  const removeStoreProfile = useEmploymentStore(state => state.removeProfile)

  const profilesByUserId = EMPLOYMENT_USE_API ? apiProfiles : storeProfiles

  const saveProfile = useCallback(
    (userId: string, patch: Partial<EmploymentProfile>) => {
      const current = profilesByUserId[userId]
      // The backend PUT is a full replace, so the patch has to be merged with
      // what is already stored before it goes out, otherwise editing a name
      // wipes the hourly rate.
      const merged: EmploymentProfile = { ...current, ...patch, userId }

      if (isEmptyProfile(merged)) {
        // Nothing left worth keeping. With no row to begin with there is
        // nothing to do; with one, the user has cleared the form and means it.
        if (!current) return
        if (EMPLOYMENT_USE_API) deleteProfile(userId)
        else removeStoreProfile(userId)
        return
      }

      if (EMPLOYMENT_USE_API) upsertProfile(merged)
      else setStoreProfile(userId, merged)
    },
    [
      profilesByUserId,
      upsertProfile,
      deleteProfile,
      setStoreProfile,
      removeStoreProfile
    ]
  )

  // Only the API side can be forbidden; the local store always answers.
  return {
    profilesByUserId,
    isLoading,
    isForbidden: EMPLOYMENT_USE_API && isForbidden,
    saveProfile
  }
}
